import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ShowToast } from "../../components/toast";
import { api } from "../api";
import { isRenderableImageUrl } from "../is-renderable-image-url";

export interface HighlightsData {
  hotelName: string;
  discoveredHighlights: string[];
  confirmedHighlights: string[] | null;
  /** Photos we scraped (Google Places + website), shown read-only. */
  scrapedImages: string[];
  userProvidedPhotos: string[];
  hasResearch: boolean;
}

/**
 * Scrub the photo lists at the data boundary so every consumer (gallery
 * tiles, photo counts, the upload-budget math) only ever sees renderable
 * absolute http(s) URLs — backend payloads have historically contained
 * comma-split URL fragments (AUT-16) that rendered as broken tiles.
 */
function sanitizeHighlightsData(data: HighlightsData): HighlightsData {
  return {
    ...data,
    scrapedImages: (data.scrapedImages ?? []).filter(isRenderableImageUrl),
    userProvidedPhotos: (data.userProvidedPhotos ?? []).filter(
      isRenderableImageUrl,
    ),
  };
}

/**
 * Loads the discovered + confirmed highlights that seed the onboarding
 * Highlights step. `discoveredHighlights` seeds the editor on first visit;
 * `confirmedHighlights` (null until saved) takes precedence on reload.
 */
export function useHighlights(userId?: string) {
  return useQuery<HighlightsData>({
    queryKey: ["highlights", userId],
    queryFn: async () => {
      if (!userId) {
        throw new Error("User ID is required");
      }
      return sanitizeHighlightsData(await api.getHighlights(userId));
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Persists the customer-confirmed highlights. On success we seed the
 * ["highlights", userId] cache with the server's canonical (deduped/capped)
 * response so the editor reflects exactly what was stored.
 */
export function useSaveConfirmedHighlights(userId?: string) {
  const queryClient = useQueryClient();

  return useMutation<HighlightsData, Error, string[]>({
    mutationFn: async (highlights: string[]) => {
      if (!userId) {
        throw new Error("User ID is required");
      }
      return api.saveConfirmedHighlights(userId, highlights);
    },
    onSuccess: (data) => {
      if (userId) {
        queryClient.setQueryData(
          ["highlights", userId],
          sanitizeHighlightsData(data),
        );
      }
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "We couldn't save your highlights. Please try again.";
      ShowToast({ type: "error", message });
    },
  });
}

/**
 * Removes one scraped ("Photos we found") image so it's no longer used in the
 * customer's ads. Optimistically drops the URL from the ["highlights", userId]
 * cache for an instant UI response, rolling back (and toasting) if the server
 * rejects it.
 */
export function useRemoveScrapedPhoto(userId?: string) {
  const queryClient = useQueryClient();

  return useMutation<
    { scrapedImages: string[] },
    Error,
    string,
    { previous?: HighlightsData }
  >({
    mutationFn: async (imageUrl: string) => {
      if (!userId) {
        throw new Error("User ID is required");
      }
      return api.removeScrapedPhoto(userId, imageUrl);
    },
    onMutate: async (imageUrl) => {
      await queryClient.cancelQueries({ queryKey: ["highlights", userId] });
      const previous = queryClient.getQueryData<HighlightsData>([
        "highlights",
        userId,
      ]);
      if (previous) {
        queryClient.setQueryData<HighlightsData>(["highlights", userId], {
          ...previous,
          scrapedImages: previous.scrapedImages.filter(
            (url) => url !== imageUrl,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _imageUrl, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["highlights", userId], context.previous);
      }
      ShowToast({
        type: "error",
        message: "We couldn't remove that photo. Please try again.",
      });
    },
    // Reconcile with the server's set, but never re-add a photo another
    // in-flight removal already dropped from the cache: filter the server list
    // down to what's still optimistically present so two quick removals don't
    // briefly flash a just-removed photo back in.
    onSuccess: ({ scrapedImages }) => {
      queryClient.setQueryData<HighlightsData>(
        ["highlights", userId],
        (prev) => {
          if (!prev) return prev;
          const stillPresent = new Set(prev.scrapedImages);
          return {
            ...prev,
            scrapedImages: scrapedImages.filter((url) => stillPresent.has(url)),
          };
        },
      );
    },
  });
}

/**
 * Server-side gate for completing the Highlights step: rejects (400) unless the
 * customer has the required photo minimum. The caller awaits this before
 * advancing and surfaces the "need at least N" message on rejection.
 */
export function useConfirmHighlightImages(userId?: string) {
  return useMutation<{ imageCount: number }, Error, void>({
    mutationFn: async () => {
      if (!userId) {
        throw new Error("User ID is required");
      }
      return api.confirmHighlights(userId);
    },
  });
}

/**
 * Removes one customer-uploaded ("add more photos") image. Same optimistic
 * pattern as [[useRemoveScrapedPhoto]] but over `userProvidedPhotos`.
 */
export function useRemoveUserPhoto(userId?: string) {
  const queryClient = useQueryClient();

  return useMutation<
    { userProvidedPhotos: string[] },
    Error,
    string,
    { previous?: HighlightsData }
  >({
    mutationFn: async (imageUrl: string) => {
      if (!userId) {
        throw new Error("User ID is required");
      }
      return api.removeUserPhoto(userId, imageUrl);
    },
    onMutate: async (imageUrl) => {
      await queryClient.cancelQueries({ queryKey: ["highlights", userId] });
      const previous = queryClient.getQueryData<HighlightsData>([
        "highlights",
        userId,
      ]);
      if (previous) {
        queryClient.setQueryData<HighlightsData>(["highlights", userId], {
          ...previous,
          userProvidedPhotos: previous.userProvidedPhotos.filter(
            (url) => url !== imageUrl,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _imageUrl, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["highlights", userId], context.previous);
      }
      ShowToast({
        type: "error",
        message: "We couldn't remove that photo. Please try again.",
      });
    },
    onSuccess: ({ userProvidedPhotos }) => {
      queryClient.setQueryData<HighlightsData>(
        ["highlights", userId],
        (prev) => {
          if (!prev) return prev;
          const stillPresent = new Set(prev.userProvidedPhotos);
          return {
            ...prev,
            userProvidedPhotos: userProvidedPhotos.filter((url) =>
              stillPresent.has(url),
            ),
          };
        },
      );
    },
  });
}
